import { useState } from "react";
import { ChevronDown, ChevronUp, ClipboardList } from "lucide-react";

const CAPITAL_LABEL = {
  working_capital: "Working Capital", asset_finance: "Asset Finance", trade_finance: "Trade Finance",
  invoice_financing: "Invoice / Receivables Financing", growth_capital: "Business Expansion / Growth Capital",
  bridge_financing: "Bridge Financing", debt_refinancing: "Debt Refinancing / Restructuring", other: "Something else",
};
const AMOUNT_LABEL = {
  kes_under_500k: "Under KES 500K", kes_500k_2m: "KES 500K–2M", kes_2m_10m: "KES 2M–10M", kes_10m_50m: "KES 10M–50M",
  usd_500k_1m: "USD 500K–1M", usd_1m_5m: "USD 1M–5M", usd_over_5m: "Over USD 5M",
};
const USD = new Set(["usd_500k_1m", "usd_1m_5m", "usd_over_5m"]);

const rows = (f) => [
  ["Role", f.role, f.role_notes],
  ["Sector", f.sector, null],
  ["Years operating", f.years_operating, null],
  ["Nature of business", f.nature_of_business, f.business_notes],
  ["Needs to grow", (f.capital_needs || []).map((k) => CAPITAL_LABEL[k] || k).join(", "), f.capital_needs_notes],
  ["Capital required", AMOUNT_LABEL[f.capital_amount] || f.capital_amount, f.capital_amount_notes],
  ["Support beyond capital", f.support_notes, null],
  ["Revenue", [f.revenue_status, f.monthly_revenue_band && `${f.monthly_revenue_band} / month`].filter(Boolean).join(" · "), f.revenue_notes],
  ["Documents held", (f.documents || []).join(", "), f.documents_notes],
];

/** Read-only summary of a submitted RM discovery form. `dark` = admin palette. */
export const DiscoverySummary = ({ discovery, dark = false, defaultOpen = false, testid = "discovery-summary" }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!discovery?.form) return null;
  const f = discovery.form;
  const t = dark
    ? { wrap: "border-neutral-800 bg-neutral-950", head: "text-neutral-200", sub: "text-neutral-500", k: "text-neutral-500", v: "text-neutral-200", note: "text-neutral-400", flag: "bg-amber-500/15 text-amber-300" }
    : { wrap: "border-slate-200 bg-white", head: "text-slate-800", sub: "text-slate-500", k: "text-slate-500", v: "text-slate-800", note: "text-slate-500", flag: "bg-amber-50 text-amber-800" };
  return (
    <div className={`mt-3 rounded-xl border ${t.wrap}`} data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left" data-testid={`${testid}-toggle`}>
        <span className={`flex items-center gap-2 text-xs font-bold ${t.head}`}><ClipboardList size={13} className="text-[#0A8B85]" /> Discovery meeting summary
          <span className={`font-normal ${t.sub}`}>· submitted {(discovery.submitted_at || "").slice(0, 10)}{discovery.by ? ` by ${discovery.by}` : ""}</span>
          {USD.has(f.capital_amount) && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.flag}`} data-testid={`${testid}-advisory-flag`}>Advisory route</span>}
        </span>
        {open ? <ChevronUp size={14} className={t.sub} /> : <ChevronDown size={14} className={t.sub} />}
      </button>
      {open && (
        <dl className="grid gap-x-6 gap-y-2 border-t px-3 py-3 text-xs sm:grid-cols-2" style={{ borderColor: dark ? "#262626" : "#e2e8f0" }} data-testid={`${testid}-body`}>
          {rows(f).filter(([, v, n]) => v || n).map(([k, v, n]) => (
            <div key={k}>
              <dt className={`text-[10px] font-bold uppercase tracking-wide ${t.k}`}>{k}</dt>
              <dd className={`mt-0.5 whitespace-pre-line ${t.v}`}>{v || "—"}</dd>
              {n && <dd className={`mt-0.5 whitespace-pre-line italic ${t.note}`}>Notes: {n}</dd>}
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};
